// UI copy is localized before user-entered content is interpolated.
// English phrases are stable keys; workspace data and route IDs remain unchanged.
import { icon } from "../features/icons.js";

export const languages = { uz: "O‘zbekcha", en: "English", ru: "Русский" };
const LANGUAGE_KEY = "dayx:language";
// "user" once the visitor picks a language; until then DayX follows their region.
const LANGUAGE_SOURCE_KEY = "dayx:language-source";
// Same rule as ivygoo: Uzbekistan → uz, Russia and the CIS → ru, everyone else → en.
const UZBEK_TIME_ZONES = new Set(["Asia/Tashkent", "Asia/Samarkand"]);
const RUSSIAN_TIME_ZONES = new Set([
  "Europe/Moscow",
  "Europe/Kaliningrad",
  "Europe/Samara",
  "Europe/Volgograd",
  "Europe/Saratov",
  "Europe/Ulyanovsk",
  "Europe/Astrakhan",
  "Europe/Kirov",
  "Asia/Yekaterinburg",
  "Asia/Omsk",
  "Asia/Novosibirsk",
  "Asia/Barnaul",
  "Asia/Tomsk",
  "Asia/Novokuznetsk",
  "Asia/Krasnoyarsk",
  "Asia/Irkutsk",
  "Asia/Chita",
  "Asia/Yakutsk",
  "Asia/Khandyga",
  "Asia/Vladivostok",
  "Asia/Ust-Nera",
  "Asia/Magadan",
  "Asia/Sakhalin",
  "Asia/Srednekolymsk",
  "Asia/Kamchatka",
  "Asia/Anadyr",
  "Europe/Minsk",
  "Asia/Almaty",
  "Asia/Qostanay",
  "Asia/Qyzylorda",
  "Asia/Aqtobe",
  "Asia/Aqtau",
  "Asia/Atyrau",
  "Asia/Oral",
  "Asia/Bishkek",
  "Asia/Dushanbe",
  "Asia/Ashgabat",
  "Asia/Baku",
  "Asia/Yerevan",
]);
/** The device time zone gives the likely language before anything is chosen. */
export function regionLanguage() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return UZBEK_TIME_ZONES.has(zone)
      ? "uz"
      : RUSSIAN_TIME_ZONES.has(zone)
        ? "ru"
        : "en";
  } catch {
    return "en";
  }
}
function chosenLanguage() {
  try {
    const saved = localStorage.getItem(LANGUAGE_KEY);
    if (!Object.hasOwn(languages, saved)) return null;
    const source = localStorage.getItem(LANGUAGE_SOURCE_KEY);
    // Before the source flag existed, a language was saved only when someone picked it.
    return source === null || source === "user" ? saved : null;
  } catch {
    return null;
  }
}
// The landing is also pre-rendered at build time in Node; that copy stays Uzbek.
let language =
  typeof window === "undefined" ? "uz" : (chosenLanguage() ?? regionLanguage());
export const getLanguage = () => language;
export const locale = () =>
  ({ uz: "uz-Latn-UZ", en: "en-US", ru: "ru-RU" })[language];
export function formatDate(date, options = {}) {
  if (language !== "uz") return date.toLocaleDateString(locale(), options);
  // Some Chromium installations lack Uzbek month data and display "M10".
  const months = [
    "yanvar",
    "fevral",
    "mart",
    "aprel",
    "may",
    "iyun",
    "iyul",
    "avgust",
    "sentabr",
    "oktabr",
    "noyabr",
    "dekabr",
  ];
  const weekdays = [
    "Yakshanba",
    "Dushanba",
    "Seshanba",
    "Chorshanba",
    "Payshanba",
    "Juma",
    "Shanba",
  ];
  const parts = [];
  if (options.weekday) parts.push(weekdays[date.getDay()]);
  let dayMonth = options.day ? String(date.getDate()) : "";
  if (options.month) {
    const month =
      options.month === "short"
        ? months[date.getMonth()].slice(0, 3)
        : months[date.getMonth()];
    dayMonth += (dayMonth ? "-" : "") + month;
  }
  if (dayMonth) parts.push(dayMonth);
  if (options.year) parts.push(String(date.getFullYear()));
  return parts.join(" ");
}
const copy = Object.fromEntries(
  `
Same plan every day|Har kuni bir xil reja|Один план на каждый день
One routine, seven days. Repeats automatically.|Bitta reja, yetti kun. Avtomatik takrorlanadi.|Один распорядок на семь дней. Повторяется автоматически.
Different day groups|Kunlarni guruhlab rejalash|Планы по группам дней
Weekdays and weekends, or any days you choose.|Ish va dam olish kunlari yoki istalgan kun guruhlari.|Будни и выходные или любые выбранные дни.
Plan each week|Har haftani alohida rejalash|Планировать каждую неделю
A separate plan for each date. A fresh start every week.|Har bir sana uchun alohida reja. Har hafta yangi boshlanish.|Отдельный план на каждую дату. Новая неделя — новый план.
Every day|Har kuni|Каждый день
Step {n} of 2|{n} / 2-qadam|Шаг {n} из 2
How do your days work?|Kunlaringiz qanday o‘tadi?|Как устроены ваши дни?
Choose a starting point. You can change it anytime.|O‘zingizga mos usulni tanlang. Istalgan payt o‘zgartirish mumkin.|Выберите подходящий способ. Его всегда можно изменить.
Make space for your week.|Haftangizga maqsad bering.|Освободите место для важных дел.
Build a routine that fits.|Sizga mos reja tuzing.|Создайте удобный распорядок.
Give each day its own priorities. Empty days can be rest days.|Har kunga alohida vazifalar belgilang. Bo‘sh kunlar dam olish uchun qolishi mumkin.|Определите задачи для каждого дня. Пустые дни можно оставить для отдыха.
Group your days, then add what matters. Your plans repeat each week.|Kunlarni guruhlang va vazifalar qo‘shing. Rejangiz har hafta takrorlanadi.|Сгруппируйте дни и добавьте задачи. План повторяется каждую неделю.
Week starting|Hafta boshlanishi|Начало недели
Copy previous week|Oldingi haftadan nusxalash|Скопировать прошлую неделю
Customize day groups|Kun guruhlarini moslash|Настроить группы дней
Choose any days together|Istalgan kunlarni birlashtiring|Объедините любые дни
Group name|Guruh nomi|Название группы
Remove group|Guruhni o‘chirish|Удалить группу
Add day group|Kun guruhi qo‘shish|Добавить группу дней
Each day belongs to one group. Selecting it moves it here.|Har bir kun bitta guruhga tegishli. Bosilsa, shu guruhga o‘tadi.|Каждый день принадлежит одной группе. Нажатие переносит его сюда.
Your plan repeats every day, including weekends.|Rejangiz har kuni, dam olish kunlari ham takrorlanadi.|План повторяется ежедневно, включая выходные.
Your priorities|Sizning vazifalaringiz|Ваши задачи
Everyday plan ideas|Kundalik reja namunalari|Идеи повседневных планов
6 examples|6 ta namuna|6 примеров
Choose an example, or write your own. Times are editable.|Namunani tanlang yoki o‘zingiz yozing. Vaqtlarni o‘zgartirishingiz mumkin.|Выберите пример или напишите свой план. Время можно изменить.
Wake up early|Erta uyg‘onish|Рано проснуться
Go for a run|Yugurishga chiqish|Выйти на пробежку
Focus on important work|Muhim ishlarga diqqat qilish|Сосредоточиться на важных делах
Learn something new|Yangi narsa o‘rganish|Узнать что-то новое
Spend time with family|Oila bilan vaqt o‘tkazish|Провести время с семьёй
Plan tomorrow|Ertangi kunni rejalash|Спланировать завтрашний день
Add all examples|Barcha namunalarni qo‘shish|Добавить все примеры
All examples added|Barcha namunalar qo‘shildi|Все примеры добавлены
Add {name}|{name} rejasini qo‘shish|Добавить: {name}
{name} added|{name} qo‘shildi|Добавлено: {name}
{n} plans|{n} ta reja|Планов: {n}
What would you like to do?|Nima qilishni rejalashtirdingiz?|Что вы хотите сделать?
Time (optional)|Vaqt (ixtiyoriy)|Время (необязательно)
Start with one priority.|Bitta muhim vazifadan boshlang.|Начните с одной важной задачи.
A small plan is a good beginning.|Kichik reja — yaxshi boshlanish.|Небольшой план — хорошее начало.
Add several plans at once|Bir nechta rejani birdan qo‘shish|Добавить несколько планов сразу
One plan per line. You can start with a time: 07:00 Read.|Har qatorda bitta reja. Vaqt bilan boshlash mumkin: 07:00 Kitob o‘qish.|По одному плану в строке. Можно начать со времени: 07:00 Чтение.
Plan names, one per line|Reja nomlari, har qatorda bittadan|Названия планов, по одному в строке
Add these plans|Bu rejalarni qo‘shish|Добавить эти планы
Maybe later|Keyinroq|Позже
Continue|Davom etish|Продолжить
Back|Orqaga|Назад
Add a plan|Reja qo‘shish|Добавить план
Plan|Reja|План
Week of {date}|{date} haftasi|Неделя с {date}
No plans for this day|Bu kun uchun reja yo‘q|На этот день нет планов
Read|Kitob o‘qish|Чтение
Work|Ish|Работа
Exercise|Mashq qilish|Тренировка
Save my plans|Rejalarimni saqlash|Сохранить мои планы
Your pace. Your priorities. Always editable.|O‘z maromingiz. O‘z maqsadlaringiz. Har doim o‘zgartirish mumkin.|Ваш темп. Ваши приоритеты. Всегда можно изменить.
Group {n}|{n}-guruh|Группа {n}
Previous week copied into empty days.|Bo‘sh kunlarga oldingi hafta rejasi ko‘chirildi.|Планы прошлой недели скопированы в пустые дни.
Give every group at least one day.|Har bir guruhga kamida bitta kun belgilang.|Выберите хотя бы один день для каждой группы.
Give your timed plan a name.|Vaqt belgilangan rejaga nom kiriting.|Укажите название плана со временем.
Your plans are ready.|Rejalaringiz tayyor.|Ваши планы готовы.
Make this planner yours.|Rejalashtirishni o‘zingizga moslang.|Настройте планировщик под себя.
Choose how your days repeat.|Kunlaringiz qanday takrorlanishini tanlang.|Выберите, как повторяются ваши дни.
Your next week starts here.|Yangi hafta shu yerdan boshlanadi.|Новая неделя начинается здесь.
Choose my rhythm|Mos usulni tanlash|Выбрать мой ритм
Plan this week|Bu haftani rejalash|Спланировать неделю
Planning setup|Rejalashtirish usuli|Способ планирования
Change my rhythm|Rejalashtirish usulini o‘zgartirish|Изменить способ планирования
Add, edit and reorder. Make the day yours.|Qo‘shing, o‘zgartiring va tartiblang. Kuningizni o‘zingiz belgilang.|Добавляйте, изменяйте и упорядочивайте. Планируйте свой день.
Repeats automatically, week after week.|Har hafta avtomatik takrorlanadi.|Автоматически повторяется каждую неделю.
Edit this week|Bu haftani o‘zgartirish|Изменить эту неделю
Previous week|Oldingi hafta|Предыдущая неделя
Next week|Keyingi hafta|Следующая неделя
Type a plan and press Enter. Details can wait.|Rejani yozib Enter bosing. Tafsilotlarni keyin qo‘shish mumkin.|Введите план и нажмите Enter. Подробности можно добавить позже.
Add a plan above. You can change it anytime.|Yuqorida reja qo‘shing. Uni istalgan payt o‘zgartirish mumkin.|Добавьте план выше. Его всегда можно изменить.
Unsaved changes|Saqlanmagan o‘zgarishlar|Несохранённые изменения
Edit directly, then save. Drag to reorder.|Joyida tahrirlang va saqlang. Tartiblash uchun torting.|Редактируйте и сохраняйте. Перетаскивайте для изменения порядка.
Drag to reorder. Use arrow keys when focused.|Tartiblash uchun torting. Belgini tanlab, klaviatura strelkalaridan ham foydalanishingiz mumkin.|Перетаскивайте для изменения порядка. На выделенном значке можно использовать клавиши со стрелками.
Please enter a plan name.|Reja nomini yozing.|Введите название плана.
Saved automatically after each change.|Har bir o‘zgarish avtomatik saqlanadi.|Каждое изменение сохраняется автоматически.
Add with details|Tafsilotlar bilan qo‘shish|Добавить с подробностями
Move {name}|{name} tartibini o‘zgartirish|Переместить {name}
Only on {date}|Faqat {date} sanasida|Только на дату {date}
Repeats on {days}|Takrorlanadigan kunlar: {days}|Повторяется в дни: {days}
Not scheduled|Rejalashtirilmagan|Не запланировано
One week, at a glance.|Bir hafta, bitta ko‘rinish.|Вся неделя перед глазами.
Each date has its own plans. Past weeks stay saved.|Har bir sananing o‘z rejasi bor. O‘tgan haftalar saqlanadi.|У каждой даты свой план. Прошлые недели сохраняются.
This week|Shu hafta|Эта неделя
Preview completion sound|Tugash ovozini tinglash|Послушать звук завершения
Add your priorities for this day. Check them off as you go.|Bu kun uchun vazifalar qo‘shing. Bajarganingizda belgilang.|Добавьте задачи на день и отмечайте выполненные.
Make room for focus.|Diqqat uchun vaqt ajrating.|Время сосредоточиться.
One session at a time. At your own pace.|Har safar bir mashg‘ulot. O‘z sur’atingizda.|По одной сессии. В своём темпе.
Ready to focus|Boshlashga tayyor|Готовы к фокусу
In progress|Davom etmoqda|В процессе
Just this moment. Just this task.|Shu lahza. Shu vazifa.|Этот момент. Эта задача.
A little time to recharge.|Kuch yig‘ish uchun tanaffus.|Время немного отдохнуть.
Choose a task to focus on|Diqqat uchun vazifa tanlang|Выберите задачу для фокуса
pause / resume|pauza / davom etish|пауза / продолжить
Focus with your daily plan|Kunlik rejangiz bilan ishlang|Работайте с планом на день
Your pace. Your focus.|O‘z sur’atingiz. O‘z diqqatingiz.|Ваш темп. Ваш фокус.
Small sessions. Meaningful progress.|Kichik mashg‘ulotlar. Sezilarli natija.|Небольшие сессии. Заметный прогресс.
YOUR RHYTHM|SIZNING MAROMINGIZ|ВАШ РИТМ
Session settings|Sessiya sozlamalari|Настройки сессий
Session {number}|{number}-sessiya|Сессия {number}
Choose a preset or set your own time.|Tayyor vaqtni tanlang yoki o‘zingiz kiriting.|Выберите готовый вариант или задайте своё время.
Focus / break (min)|Diqqat / tanaffus (daq)|Фокус / перерыв (мин)
Quick settings|Tayyor vaqtlar|Готовые варианты
Focus time|Diqqat vaqti|Время фокуса
Break time|Tanaffus|Перерыв
Repeat this session|Takrorlash soni|Количество повторов
Add session|Sessiya qo‘shish|Добавить сессию
Repeat sequence|Tartibni takrorlash|Повторять последовательность
Start again after the last session.|Oxirgi sessiyadan keyin qayta boshlash.|Начать заново после последней сессии.
{work} min focus · {rest} min rest · {repeats}×|{work} daq diqqat · {rest} daq tanaffus · {repeats}×|{work} мин фокуса · {rest} мин отдыха · {repeats}×
{work} minutes focus, {rest} minutes rest|{work} daqiqa diqqat, {rest} daqiqa tanaffus|{work} минут фокуса, {rest} минут отдыха
Decrease {label}|{label}: kamaytirish|{label}: уменьшить
Increase {label}|{label}: oshirish|{label}: увеличить
Delete {session}|{session}ni o‘chirish|Удалить: {session}
Session sequence|Mashg‘ulotlar tartibi|Порядок сессий
Add Block|Bosqich qo‘shish|Добавить блок
Delete block|Bosqichni o‘chirish|Удалить блок
sessions|mashg‘ulot|сессий
min of focus|daq diqqat|мин фокуса
Changes apply to your next sequence.|O‘zgarishlar keyingi tartibga qo‘llanadi.|Изменения применятся к следующей последовательности.
Session options|Mashg‘ulot sozlamalari|Настройки сессии
A small window. More room to work.|Kichik oyna. Ishlash uchun ko‘proq joy.|Маленькое окно. Больше места для работы.
Drag it to any corner. Keep this tab open.|Istalgan burchakka suring. Sahifani ochiq qoldiring.|Перетащите в любой угол. Оставьте вкладку открытой.
Expand timer|Oynani kengaytirish|Развернуть таймер
Compact timer|Oynani kichraytirish|Свернуть таймер
Close floating timer|Kichik oynani yopish|Закрыть плавающий таймер
Above your apps, at your own pace.|Boshqa ilovalar ustida, o‘z sur’atingizda.|Поверх приложений, в вашем темпе.

Show time|Vaqtni ko‘rsatish|Показать время
Hide time|Vaqtni yashirish|Скрыть время
Floating timer|Kichik oynada ochish|Плавающий таймер
Pomodoro timer illustration|Pomodoro taymeri rasmi|Изображение таймера Помодоро
Keep your timer above other apps. Leave this tab open.|Taymer boshqa ilovalar ustida turadi. Bu sahifani ochiq qoldiring.|Таймер поверх других приложений. Оставьте эту вкладку открытой.
Ready|Tayyor|Готово
Starting audio…|Ovoz yoqilmoqda…|Запуск звука…
Paused|Pauzada|На паузе
Open DayX in desktop Chrome or Edge to use the floating timer.|Kichik oyna uchun DayX’ni kompyuterda Chrome yoki Edge orqali oching.|Для плавающего таймера откройте DayX в Chrome или Edge на компьютере.
The floating timer could not open. Please try again.|Kichik oyna ochilmadi. Qayta urinib ko‘ring.|Не удалось открыть таймер. Попробуйте ещё раз.

Today|Bugun|Сегодня
Weekly|Haftalik reja|Неделя
Plan Table|Rejalar jadvali|Таблица планов
Exam Dates|Imtihonlar|Экзамены
All Days|Kunlar tarixi|История дней
Manage Plans|Rejalarni boshqarish|Управление планами
Pomodoro|Diqqat taymeri|Таймер фокуса
Settings|Sozlamalar|Настройки
Workspace|Ish maydoni|Рабочее пространство
WORKSPACE|ISH MAYDONI|РАБОЧЕЕ ПРОСТРАНСТВО
YOUR DAILY WORKSPACE|KUNLIK ISH MAYDONINGIZ|ВАШЕ ПРОСТРАНСТВО НА КАЖДЫЙ ДЕНЬ
Guest workspace|Mehmon profili|Гостевой профиль
Account|Hisob|Аккаунт
Account and saving|Hisob va saqlash|Аккаунт и сохранение
Account & saving|Hisob va saqlash|Аккаунт и сохранение
Manage account|Hisobni boshqarish|Управление аккаунтом
Your workspace|Ish maydoningiz|Ваше пространство
Your routine, your way.|Sizga mos kun tartibi.|Ваш собственный ритм.
Your day starts here.|Kuningiz shu yerdan boshlanadi.|Ваш день начинается здесь.
Plan your weekend.|Dam olish kunlarini rejalang.|Планируйте выходные.
Plan your weekdays.|Ish kunlarini rejalang.|Планируйте будни.
Date navigation|Sana boshqaruvi|Навигация по датам
A little intention. A routine that feels like you.|Aniq maqsad. Sizga mos kun tartibi.|Чёткая цель. Ритм, который подходит вам.
Make today count.|Bugunni mazmunli o‘tkazing.|Проведите день с пользой.
A day with intention.|Maqsadli kun sari.|День с ясной целью.
Your pace. Your priorities. Your next small win.|O‘z sur’atingiz. Muhim rejalaringiz. Yangi yutuqlaringiz.|Ваш темп. Ваши приоритеты. Ваши достижения.
· Future completion is locked|· Kelajakdagi rejalar hali belgilanmaydi|· Будущие планы пока нельзя отмечать
Choose a focus|Vazifani tanlash|Выбрать задачу
Start focus|Diqqatni jamlash|Начать фокус
Daily progress|Kunlik natija|Прогресс за день
DAILY MOMENTUM|KUNLIK NATIJA|ПРОГРЕСС ЗА ДЕНЬ
You made it happen.|Barcha rejalar bajarildi.|Все планы выполнены.
Every step counts.|Har bir qadam muhim.|Каждый шаг важен.
of|/|из
plans completed|ta reja bajarildi|планов выполнено
Focus timer|Diqqat taymeri|Таймер фокуса
Focus Timer|Diqqat taymeri|Таймер фокуса
YOUR FOCUS SPACE|DIQQAT MAYDONI|ПРОСТРАНСТВО ФОКУСА
Pause focus timer|Taymerni to‘xtatish|Приостановить таймер
Start focus timer|Taymerni boshlash|Запустить таймер
One thing at a time.|Bir paytda bitta vazifa.|Одна задача за раз.
Today’s focus statistics|Bugungi diqqat natijalari|Статистика фокуса за сегодня
TODAY’S SMALL WINS|BUGUNGI YUTUQLAR|ДОСТИЖЕНИЯ СЕГОДНЯ
sessions|sessiya|сессии
focused|diqqat vaqti|в фокусе
Make room for deep work.|Muhim ishga vaqt ajrating.|Выделите время для глубокой работы.
Your daily rhythm|Kunlik rejalaringiz|Ваши планы на день
Add plan|Reja qo‘shish|Добавить план
+ Add plan|+ Reja qo‘shish|+ Добавить план
+ Add your first plan|+ Birinchi rejani qo‘shish|+ Добавить первый план
Create your plan|Rejangizni yarating|Создайте свой план
Create your plan.|Rejangizni yarating.|Создайте свой план.
Create plan|Reja yaratish|Создать план
YOUR FRESH START|YANGI BOSHLANISH|НОВОЕ НАЧАЛО
/ A FRESH START|/ YANGI BOSHLANISH|/ НОВОЕ НАЧАЛО
Make room for what matters. Add your first plan and build a daily routine around your goals.|Muhim ishlar uchun vaqt ajrating. Birinchi rejangizni qo‘shing va maqsadlaringizga mos kun tartibini tuzing.|Выделите время для главного. Добавьте первый план и создайте ритм вокруг своих целей.
Start with one priority. You can change it anytime.|Bitta muhim vazifadan boshlang. Uni istalgan payt o‘zgartira olasiz.|Начните с одной важной задачи. Её можно изменить в любой момент.
YOUR DAILY PLAN|KUNLIK REJANGIZ|ВАШ ПЛАН НА ДЕНЬ
A little room|Yangi imkoniyatlar|Немного места
for|uchun|для
possibility.|joy oching.|возможностей.
YOUR PACE. YOUR PRIORITIES.|O‘Z SUR’ATINGIZ. O‘Z REJALARINGIZ.|ВАШ ТЕМП. ВАШИ ПРИОРИТЕТЫ.
Choose a priority|Muhim vazifani tanlang|Выберите приоритет
One meaningful task is enough to begin.|Boshlash uchun bitta muhim vazifa yetarli.|Для начала достаточно одной важной задачи.
Find your rhythm|Kun tartibini belgilang|Найдите свой ритм
Set a routine for weekdays or weekends.|Ish kunlari yoki dam olish kunlari uchun reja tuzing.|Составьте план на будни или выходные.
See your progress|Natijangizni kuzating|Следите за прогрессом
Check off your plans as you go.|Bajargan rejalaringizni belgilab boring.|Отмечайте выполненные планы.
Add exam date|Imtihon sanasini qo‘shish|Добавить дату экзамена
Add an exam date|Imtihon sanasini qo‘shish|Добавить дату экзамена
NEXT EXAM|KEYINGI IMTIHON|СЛЕДУЮЩИЙ ЭКЗАМЕН
EXAM DAY|IMTIHON KUNI|ДЕНЬ ЭКЗАМЕНА
DAYS LEFT|KUN QOLDI|ДНЕЙ ОСТАЛОСЬ
DAY LEFT|KUN QOLDI|ДЕНЬ ОСТАЛСЯ
View exam dates|Imtihonlarni ko‘rish|Просмотреть экзамены
View all|Barchasini ko‘rish|Посмотреть все
EXAM DATES|IMTIHONLAR|ЭКЗАМЕНЫ
Keep your deadlines in sight|Muhim sanalarni nazoratda tuting|Следите за важными датами
Add a date to start the countdown.|Sana qo‘shing va qolgan vaqtni kuzating.|Добавьте дату и следите за обратным отсчётом.
PAST EXAM|O‘TGAN IMTIHON|ПРОШЕДШИЙ ЭКЗАМЕН
NEXT UP|KEYINGISI|СЛЕДУЮЩИЙ
UPCOMING|KUTILAYOTGAN|ПРЕДСТОЯЩИЙ
TODAY|BUGUN|СЕГОДНЯ
DAY AGO|KUN OLDIN|ДЕНЬ НАЗАД
DAYS AGO|KUN OLDIN|ДНЕЙ НАЗАД
Edit|Tahrirlash|Изменить
Delete|O‘chirish|Удалить
Know what comes next.|Muhim sanalar yodingizda.|Важные даты под контролем.
Add your exams and see exactly how many days remain.|Imtihon sanalarini kiriting va qancha vaqt qolganini kuzating.|Добавьте экзамены и узнайте, сколько дней осталось.
+ Add exam|+ Imtihon qo‘shish|+ Добавить экзамен
DAYS TO GO|KUN QOLDI|ДНЕЙ ДО ЭКЗАМЕНА
Upcoming exams|Yaqinlashayotgan imtihonlar|Предстоящие экзамены
scheduled|ta belgilangan|запланировано
No upcoming exams|Hozircha imtihon yo‘q|Пока нет экзаменов
Add a date to start your countdown.|Imtihon sanasini qo‘shib boshlang.|Добавьте дату экзамена, чтобы начать отсчёт.
Past exams|O‘tgan imtihonlar|Прошедшие экзамены
saved|ta saqlangan|сохранено
Future days cannot be completed yet|Kelajakdagi rejalarni hali belgilab bo‘lmaydi|Будущие планы пока нельзя отмечать
Complete|Bajarildi deb belgilash|Отметить выполненным
Complete {name}|{name} — bajarilgan deb belgilash|Отметить «{name}» выполненным
Drag {name} to reorder|{name} — tartibni o‘zgartirish uchun suring|Перетащите «{name}» для изменения порядка
Move {name} up|{name} — yuqoriga ko‘chirish|Переместить «{name}» выше
Move {name} down|{name} — pastga ko‘chirish|Переместить «{name}» ниже
Deep work|Chuqur diqqat|Глубокая работа
Routine|Kun tartibi|Рутина
YOUR WEEK, WITH INTENTION|HAFTALIK REJANGIZ|ВАШ ПЛАН НА НЕДЕЛЮ
Week of|Hafta:|Неделя:
Build a rhythm for the days ahead.|Kelgusi kunlar uchun reja tuzing.|Создайте ритм для предстоящих дней.
Seven days. Plenty of possibility.|Yetti kun. Yangi imkoniyatlar.|Семь дней. Новые возможности.
THE BIGGER PICTURE|UMUMIY NATIJA|ОБЩАЯ КАРТИНА
Your week, at a glance.|Haftangiz bir qarashda.|Ваша неделя в одном обзоре.
Every small win becomes momentum.|Kichik yutuqlar katta natijalarga yetaklaydi.|Маленькие победы ведут к большим результатам.
Daily completion percentages|Kunlik bajarilish foizi|Процент выполнения по дням
A GENTLE REMINDER|ESLATMA|НАПОМИНАНИЕ
Consistency over|Davomiylik|Последовательность важнее
intensity.|muhim.|интенсивности.
You don’t need a perfect week. Just a clear next step, and a little space to begin again.|Mukammal hafta shart emas. Keyingi qadam aniq bo‘lsin, davom etish uchun imkon doim bor.|Идеальная неделя не обязательна. Достаточно ясного следующего шага и возможности начать снова.
Shape your routine|Kun tartibini tuzish|Создать свой ритм
ALL DAYS / YOUR JOURNEY|KUNLAR TARIXI / NATIJALARINGIZ|ИСТОРИЯ ДНЕЙ / ВАШ ПРОГРЕСС
Your progress begins here.|Natijalaringiz shu yerdan boshlanadi.|Ваш прогресс начинается здесь.
Your timeline grows with every plan you complete.|Har bir bajarilgan reja tarixingizga qo‘shiladi.|Каждый выполненный план пополняет вашу историю.
Daily Timeline|Kunlar tarixi|История дней
A little progress, day after day. Your 61-day view.|Har kuni yangi natija. 61 kunlik kuzatuv.|Прогресс день за днём. Обзор за 61 день.
completed ·|ta bajarildi ·|выполнено ·
Open day|Kunni ochish|Открыть день
View|Ko‘rish:|Посмотреть
plans|ta reja|планов
MANAGE PLANS|REJALARNI BOSHQARISH|УПРАВЛЕНИЕ ПЛАНАМИ
Create a shared routine for weekdays or weekends.|Ish va dam olish kunlari uchun alohida rejalar tuzing.|Создайте отдельные планы для будней и выходных.
Choose plan group|Kunlar guruhini tanlash|Выбрать группу дней
plan|rejasi|план
. Changes appear in upcoming weeks automatically.|. O‘zgarishlar kelgusi haftalarga avtomatik qo‘llanadi.|. Изменения автоматически появятся в следующих неделях.
Plan order also sets the table column order.|Rejalar tartibi jadval ustunlari tartibini ham belgilaydi.|Порядок планов также задаёт порядок столбцов таблицы.
Drag to reorder (use the ⋮⋮ handle on touch screens), or use ↑ and ↓.|Tartibni o‘zgartirish uchun ⋮⋮ orqali suring yoki ↑ va ↓ tugmalaridan foydalaning.|Перетащите за ⋮⋮ или используйте ↑ и ↓, чтобы изменить порядок.
Drag to reorder|Tartibni o‘zgartirish uchun suring|Перетащите для изменения порядка
No plans yet.|Hozircha reja yo‘q.|Планов пока нет.
AUTOMATIC STUDY FLOW ·|AVTOMATIK O‘QISH TARTIBI ·|АВТОМАТИЧЕСКАЯ УЧЕБНАЯ СЕССИЯ ·
Today's study sequence|Bugungi o‘qish tartibi|Учебный план на сегодня
selected tasks · Study time includes 5 min rest for short tasks or 10 min for longer tasks. The flow continues automatically.|ta vazifa · Qisqa vazifalarda 5, uzun vazifalarda 10 daqiqa tanaffus hisobga olinadi. Tartib avtomatik davom etadi.|задач · Время включает 5 минут отдыха для коротких задач и 10 для длинных. Переходы выполняются автоматически.
Restart flow|Qayta boshlash|Перезапустить
Start Study Flow|O‘qishni boshlash|Начать учёбу
Choose tasks and set minutes|Vazifalar va vaqtni tanlash|Выбрать задачи и время
min|daq|мин
m|daq|мин
POMODORO|DIQQAT TAYMERI|ТАЙМЕР ФОКУСА
Continuous sequence enabled|Avtomatik takrorlash yoqilgan|Автоматическое повторение включено
Stops after final block|Oxirgi bosqichdan keyin tugaydi|Завершается после последнего блока
Current block|Joriy bosqich|Текущий блок
ON|Yoqilgan|Вкл.
OFF|O‘chirilgan|Выкл.
PAUSE|TO‘XTATISH|ПАУЗА
RESUME|DAVOM ETISH|ПРОДОЛЖИТЬ
START|BOSHLASH|НАЧАТЬ
RESET|QAYTA BOSHLASH|СБРОСИТЬ
SEQUENCE|BOSQICHLAR|ПОСЛЕДОВАТЕЛЬНОСТЬ
+ Add Block|+ Bosqich qo‘shish|+ Добавить блок
Block|Bosqich|Блок
Work|Ish vaqti|Работа
Rest|Tanaffus|Отдых
Repeats|Takrorlash|Повторы
Loop Sequence|Bosqichlarni takrorlash|Повторять последовательность
Completion Sound|Tugash ovozi|Звук завершения
Notifications|Bildirishnomalar|Уведомления
Plays only during Work sessions.|Faqat ish vaqtida ijro etiladi.|Воспроизводится только во время работы.
Playing|Ijro etilmoqda|Воспроизводится
Off|O‘chirilgan|Выключено
SETTINGS|SOZLAMALAR|НАСТРОЙКИ
Preferences|Shaxsiy sozlamalar|Ваши настройки
Your preferences, account and backups.|Til, hisob va ma’lumotlaringizni boshqaring.|Управляйте языком, аккаунтом и данными.
Theme|Ko‘rinish|Оформление
Appearance|Ko‘rinish|Оформление
Appearance mode|Ko‘rinish rejimi|Режим оформления
Light|Oq|Светлый
Dark|Qora|Тёмный
Automatic|Avtomatik|Автоматически
Choose light, dark or let DayX switch automatically.|Oq yoki qora rejimni tanlang, yoxud DayX avtomatik almashtirsin.|Выберите светлый или тёмный режим либо автоматическое переключение.
Automatic switching|Avtomatik almashish|Автоматическое переключение
Custom schedule|Belgilangan vaqt bo‘yicha|По расписанию
Follow device|Qurilma rejimiga moslash|Как на устройстве
Dark mode starts|Qora rejim boshlanishi|Начало тёмного режима
Light mode starts|Oq rejim boshlanishi|Начало светлого режима
Times follow your device’s local clock. DayX switches even while you are working.|Vaqtlar qurilmangizning mahalliy soati bo‘yicha. Ishlayotganingizda ham rejim avtomatik almashadi.|Время соответствует местным часам устройства. Режим переключается даже во время работы.
DayX follows the light or dark appearance selected on your device.|DayX qurilmangizda tanlangan oq yoki qora rejimga moslashadi.|DayX следует светлому или тёмному режиму вашего устройства.
Dark appearance is active.|Qora rejim yoqilgan.|Включён тёмный режим.
Light appearance is active.|Oq rejim yoqilgan.|Включён светлый режим.
Following your device appearance.|Qurilma rejimiga moslashmoqda.|Как на вашем устройстве.
Switches to light at {time}.|{time} da oq rejimga o‘tadi.|В {time} переключится на светлый режим.
Switches to dark at {time}.|{time} da qora rejimga o‘tadi.|В {time} переключится на тёмный режим.
Saved automatically in this browser and applied across the whole website.|Tanlov shu brauzerda avtomatik saqlanadi va butun saytga qo‘llanadi.|Выбор автоматически сохраняется в этом браузере и применяется ко всему сайту.
Appearance: {mode}. Switch to {next}.|Ko‘rinish: {mode}. {next} rejimiga o‘tish.|Оформление: {mode}. Переключить на {next}.
Choose two different valid times for light and dark mode.|Oq va qora rejim uchun ikkita turli, to‘g‘ri vaqtni belgilang.|Укажите два разных корректных времени для светлого и тёмного режима.
Appearance changed for this visit. Your browser could not save the preference.|Ko‘rinish bu tashrif uchun o‘zgardi. Brauzer tanlovni saqlay olmadi.|Оформление изменено для этого посещения. Браузер не смог сохранить выбор.
Dark surfaces, soft contrast and less glare for night work.|Tungi ish uchun qora fon, yumshoq kontrast va kamroq yorqinlik.|Тёмные поверхности, мягкий контраст и меньше яркости для ночной работы.
Night color palette|Tungi ranglar palitrasi|Ночная цветовая палитра
Charcoal|To‘q kulrang|Графитовый
Soft white|Yumshoq oq|Мягкий белый
Night mode|Tungi rejim|Ночной режим
A calm canvas, forest tones, and a little room to breathe.|Sokin och fon va tabiiy yashil ranglar.|Светлый фон и естественные зелёные оттенки.
Forest and Linen color palette|DayX ranglar uyg‘unligi|Цветовая палитра DayX
Forest|To‘q yashil|Тёмно-зелёный
Sage|Och yashil|Светло-зелёный
Linen|Och fon|Светлый фон
Forest & Linen|DayX ko‘rinishi|Оформление DayX
Defaults can be adjusted directly in the Pomodoro sequence.|Taymer bosqichlarida vaqt va takrorlashni sozlang.|Настройте время и повторы в блоках таймера.
Sound|Ovoz|Звук
Pomodoro Loop|Taymerni takrorlash|Повтор таймера
Data|Ma’lumotlar|Данные
Export everything as JSON or restore a previous export.|Ma’lumotlarni JSON faylga saqlang yoki avvalgi nusxani tiklang.|Сохраните данные в JSON или восстановите резервную копию.
Export Data|Eksport qilish|Экспорт данных
Import Data|Import qilish|Импорт данных
Reset Data|Ma’lumotlarni tozalash|Очистить данные
Keyboard shortcuts|Tezkor tugmalar|Горячие клавиши
N next day • P previous day • T today • Space pause/resume Pomodoro • R reset Pomodoro|N — keyingi kun · P — oldingi kun · T — bugun · Space — taymerni to‘xtatish/davom etish · R — taymerni tiklash|N — следующий день · P — предыдущий · T — сегодня · Space — пауза/продолжить · R — сброс таймера
Edit plan|Rejani tahrirlash|Изменить план
This plan appears every|Reja takrorlanadi:|План повторяется:
Monday through Friday|dushanbadan jumagacha|с понедельника по пятницу
Saturday and Sunday|shanba va yakshanba|в субботу и воскресенье
. You can edit it anytime.|. Istalgan payt tahrirlashingiz mumkin.|. Его можно изменить в любой момент.
Plan name|Reja nomi|Название плана
For example: Read for 20 minutes|Masalan: 20 daqiqa kitob o‘qish|Например: читать 20 минут
Time / schedule|Vaqt / jadval|Время / расписание
Optional|Ixtiyoriy|Необязательно
Notes|Izoh|Заметки
Highlight as a study task in the table|Jadvalda o‘qish vazifasi sifatida ajratish|Выделить в таблице как учебную задачу
Cancel|Bekor qilish|Отмена
Save changes|Saqlash|Сохранить изменения
Task Name is required.|Reja nomini kiriting.|Введите название плана.
Plan updated.|Reja yangilandi.|План обновлён.
Plan added.|Reja qo‘shildi.|План добавлен.
EXAM DATE|IMTIHON SANASI|ДАТА ЭКЗАМЕНА
Edit exam|Imtihonni tahrirlash|Изменить экзамен
Add an exam|Imtihon qo‘shish|Добавить экзамен
Give this date a name. The countdown updates automatically each day.|Nom va sanani kiriting. Qolgan vaqt har kuni avtomatik yangilanadi.|Укажите название и дату. Обратный отсчёт обновляется каждый день.
Exam name|Imtihon nomi|Название экзамена
For example: IELTS|Masalan: IELTS|Например: IELTS
Exam date|Imtihon sanasi|Дата экзамена
(optional)|(ixtiyoriy)|(необязательно)
Location, target score, or anything to remember|Manzil, maqsadli ball yoki eslatma|Место, целевой балл или напоминание
Add exam|Imtihon qo‘shish|Добавить экзамен
Enter an exam name.|Imtihon nomini kiriting.|Введите название экзамена.
Choose a valid exam date.|To‘g‘ri imtihon sanasini tanlang.|Выберите корректную дату экзамена.
This exam is already in your list.|Bu imtihon ro‘yxatda mavjud.|Этот экзамен уже в списке.
Exam updated.|Imtihon yangilandi.|Экзамен обновлён.
Exam added.|Imtihon qo‘shildi.|Экзамен добавлен.
Delete this exam?|Imtihon o‘chirilsinmi?|Удалить этот экзамен?
on|sana:|дата:
will be removed from your countdowns.|imtihonlar ro‘yxatidan o‘chiriladi.|будет удалён из списка экзаменов.
Delete exam|Imtihonni o‘chirish|Удалить экзамен
Exam deleted.|Imtihon o‘chirildi.|Экзамен удалён.
Delete this plan?|Reja o‘chirilsinmi?|Удалить этот план?
will be removed from the|quyidagi guruhdan o‘chiriladi:|будет удалён из группы
plan. Past completion data remains stored.|. Oldingi bajarilish natijalari saqlanadi.|. История выполнения сохранится.
Recurring task deleted.|Takroriy reja o‘chirildi.|Повторяющийся план удалён.
Use this guest workspace or create an account to open your planner on other devices.|Mehmon sifatida davom eting yoki boshqa qurilmalarda ham foydalanish uchun hisob yarating.|Продолжайте как гость или создайте аккаунт для работы на других устройствах.
Review your changes|O‘zgarishlarni tekshiring|Проверьте изменения
Welcome back|Xush kelibsiz|С возвращением
Save your progress|Natijalarni saqlang|Сохраните прогресс
Plans changed on another device. Your local changes are still saved here. Choose the version to continue with; export a backup from Settings first if you need both.|Rejalar boshqa qurilmada o‘zgargan. Mahalliy o‘zgarishlar saqlangan. Davom etish uchun nusxani tanlang; ikkala nusxa kerak bo‘lsa, avval Sozlamalarda eksport qiling.|Планы изменены на другом устройстве. Локальные изменения сохранены. Выберите версию; если нужны обе, сначала экспортируйте резервную копию в настройках.
Use server version|Server nusxasini tanlash|Использовать версию сервера
Keep device changes|Qurilma nusxasini saqlash|Оставить версию устройства
Google account connected.|Google hisobi ulangan.|Аккаунт Google подключён.
Connect Google to log in with one click.|Bir bosishda kirish uchun Google hisobini ulang.|Подключите Google для входа в один клик.
Connect Google|Google’ni ulash|Подключить Google
Close|Yopish|Закрыть
Sign out|Hisobdan chiqish|Выйти
Email|Email|Email
Password · 8 characters minimum|Parol · kamida 8 belgi|Пароль · минимум 8 символов
Create account|Hisob yaratish|Создать аккаунт
Sign in|Kirish|Войти
Already have an account? Sign in|Hisobingiz bormi? Kiring|Уже есть аккаунт? Войдите
Create a new account|Yangi hisob yaratish|Создать новый аккаунт
Your changes are still pending. Reconnect or review the saving status first.|O‘zgarishlar hali saqlanmagan. Aloqani yoki saqlash holatini tekshiring.|Изменения ещё не сохранены. Проверьте подключение или статус сохранения.
Save your pending changes before signing out.|Chiqishdan oldin o‘zgarishlarni saqlang.|Сохраните изменения перед выходом.
Google sign-in is coming soon.|Google orqali kirish tez orada yoqiladi.|Вход через Google скоро появится.
Google sign-in is not available yet. Please use email below.|Google orqali kirish hali yoqilmagan. Email orqali davom eting.|Вход через Google пока недоступен. Используйте email.
Your Google sign-in expired. Please try again.|Google orqali kirish vaqti tugadi. Qayta urinib ko‘ring.|Время входа через Google истекло. Попробуйте снова.
Google sign-in was cancelled. You can try again or use email.|Google orqali kirish bekor qilindi. Qayta urining yoki emaildan foydalaning.|Вход через Google отменён. Повторите или используйте email.
We couldn't complete Google sign-in. Please try again.|Google orqali kirish amalga oshmadi. Qayta urinib ko‘ring.|Не удалось войти через Google. Попробуйте снова.
This email already has an account. Log in with your password, then connect Google from your account settings.|Bu email bilan hisob mavjud. Parol bilan kiring va hisob sozlamalarida Google’ni ulang.|Аккаунт с этим email уже существует. Войдите с паролем и подключите Google в настройках.
Choose the Google account with the same email as your workspace.|Hisobingizdagi email bilan bir xil Google hisobini tanlang.|Выберите аккаунт Google с тем же email.
You're already signed in. Continue to your workspace.|Hisobga kirgansiz. Ish maydoniga o‘ting.|Вы уже вошли. Перейдите в рабочее пространство.
Your changes are still saving. Please try again in a moment.|O‘zgarishlar saqlanmoqda. Birozdan so‘ng qayta urining.|Изменения сохраняются. Попробуйте через мгновение.
A SPACE FOR YOUR AMBITION|MAQSADLARINGIZ UCHUN MAYDON|ПРОСТРАНСТВО ДЛЯ ВАШИХ ЦЕЛЕЙ
Good days start|Yaxshi kunlar|Хорошие дни начинаются
with|aniq|с
intention.|maqsaddan boshlanadi.|ясной цели.
Your plans. Your focus. Your next small win.|Rejalar, diqqat va yangi yutuqlar.|Ваши планы. Ваш фокус. Ваши победы.
All together, in a space that feels like you.|Barchasi sizga mos yagona maydonda.|Всё вместе в вашем пространстве.
YOUR NEXT CHAPTER|YANGI BOSQICH|НОВАЯ ГЛАВА
One priority.|Bitta muhim vazifa.|Один приоритет.
A little progress.|Har kuni natija.|Прогресс каждый день.
Make room for what matters|Muhim ishga vaqt ajrating|Выделите время для главного
Build a rhythm of your own|O‘zingizga mos tartib tuzing|Создайте собственный ритм
Take the next small step|Keyingi qadamni qo‘ying|Сделайте следующий шаг
YOUR PACE. YOUR POSSIBILITIES.|O‘Z SUR’ATINGIZ. YANGI IMKONIYATLAR.|ВАШ ТЕМП. ВАШИ ВОЗМОЖНОСТИ.
Account navigation|Hisob navigatsiyasi|Навигация аккаунта
Already have an account?|Hisobingiz bormi?|Уже есть аккаунт?
New to DayX?|DayX’da yangimisiz?|Впервые в DayX?
Log in|Kirish|Войти
Sign up|Ro‘yxatdan o‘tish|Регистрация
← Back to home|← Bosh sahifaga|← На главную
Create your account.|Hisobingizni yarating.|Создайте аккаунт.
Welcome back.|Xush kelibsiz.|С возвращением.
A little clarity for your day. A place for your goals.|Kuningizni tartibga soling. Maqsadlaringizga joy oching.|Наведите порядок в своём дне. Выделите место для целей.
Your plans and progress, right where you left them.|Rejalaringiz va natijalaringiz sizni kutmoqda.|Ваши планы и прогресс ждут вас.
Continue with Google|Google orqali davom etish|Продолжить с Google
Checking Google sign-in…|Google orqali kirish tekshirilmoqda…|Проверяем вход через Google…
or continue with email|yoki email orqali|или через email
Email address|Email manzili|Адрес email
Password|Parol|Пароль
8 characters minimum|Kamida 8 ta belgi|Минимум 8 символов
Create a strong password|Parol yarating|Создайте пароль
Enter your password|Parolingizni kiriting|Введите пароль
Show password|Parolni ko‘rsatish|Показать пароль
Hide password|Parolni yashirish|Скрыть пароль
Show|Ko‘rsatish|Показать
Hide|Yashirish|Скрыть
Your guest plans move into your new account.|Mehmon sifatida tuzgan rejalaringiz hisobga o‘tadi.|Гостевые планы перенесутся в ваш аккаунт.
Your account keeps your plans across devices.|Rejalaringiz barcha qurilmalarda hisobingizda saqlanadi.|Ваши планы доступны на всех устройствах.
Continue as a guest|Mehmon sifatida davom etish|Продолжить как гость
Less noise. More focus.|Aniq reja. Kuchli diqqat.|Меньше шума. Больше фокуса.
Continue to your workspace →|Ish maydoniga o‘tish →|Перейти в рабочее пространство →
You're already signed in.|Hisobga kirgansiz.|Вы уже вошли.
Google sign-in is coming soon. You can use email today.|Google orqali kirish tez orada yoqiladi. Hozir email orqali kirishingiz mumkin.|Вход через Google скоро появится. Сейчас используйте email.
Google sign-in is temporarily unavailable.|Google orqali kirish vaqtincha ishlamayapti.|Вход через Google временно недоступен.
Connecting to Google…|Google’ga ulanmoqda…|Подключаемся к Google…
Creating your account…|Hisob yaratilmoqda…|Создаём аккаунт…
Logging in…|Hisobga kirilmoqda…|Входим…
Ready when you are|Boshlashga tayyor|Готовы к началу
Excellent|Ajoyib|Отлично
Very Good|Juda yaxshi|Очень хорошо
Good|Yaxshi|Хорошо
Average|O‘rtacha|Средний результат
Low Progress|Davom eting|Продолжайте
1 day left|1 kun qoldi|Остался 1 день
days left|kun qoldi|дней осталось
day|kun|день
days|kun|дней
ago|oldin|назад
Add a Pomodoro block first.|Avval taymer bosqichini qo‘shing.|Сначала добавьте блок таймера.
Work session completed - Rest begins.|Ish vaqti tugadi — tanaffus boshlandi.|Работа завершена — начинается отдых.
Work session completed - Next work session begins.|Bosqich tugadi — keyingi ish bosqichi boshlandi.|Блок завершён — начинается следующий.
Pomodoro sequence complete.|Taymer bosqichlari yakunlandi.|Последовательность таймера завершена.
Rest completed - Work session begins.|Tanaffus tugadi — ish bosqichi boshlandi.|Отдых завершён — начинается работа.
Choose at least one study task.|Kamida bitta o‘qish vazifasini tanlang.|Выберите хотя бы одну учебную задачу.
Study Flow started:|O‘qish boshlandi:|Учебная сессия началась:
Rest time:|Tanaffus:|Отдых:
min after|daqiqalik tanaffus, vazifa:|минут после
Study Flow complete.|O‘qish tartibi yakunlandi.|Учебная сессия завершена.
Next:|Keyingi:|Далее:
Study Flow:|O‘qish:|Учёба:
Study Flow timer|O‘qish taymeri|Учебный таймер
COMPLETE|YAKUNLANDI|ЗАВЕРШЕНО
REST TIME|TANAFFUS|ОТДЫХ
FOCUS TIME|DIQQAT VAQTI|ВРЕМЯ ФОКУСА
Expand|Kengaytirish|Развернуть
Minimize|Kichraytirish|Свернуть
Study Flow|O‘qish tartibi|Учебная сессия
Close Study Flow|O‘qish taymerini yopish|Закрыть учебный таймер
Rest after|Tanaffus, vazifa:|Отдых после
Sequence complete|Bosqichlar tugadi|Последовательность завершена
Final rest|Oxirgi tanaffus|Последний отдых
Rest:|Tanaffus:|Отдых:
· Then|· So‘ng|· Затем
Final task|Oxirgi vazifa|Последняя задача
Pause|To‘xtatish|Пауза
Resume|Davom etish|Продолжить
Skip phase →|Bosqichni o‘tkazish →|Пропустить этап →
Keep at least one block.|Kamida bitta bosqich qolsin.|Оставьте хотя бы один блок.
Focus on task|Vazifani tanlash|Выбрать задачу для фокуса
Today's task|Bugungi vazifa|Задача на сегодня
No selected task|Vazifa tanlanmagan|Задача не выбрана
Save|Saqlash|Сохранить
Data exported.|Ma’lumotlar eksport qilindi.|Данные экспортированы.
Reset all DayX data? This cannot be undone.|Barcha DayX ma’lumotlari o‘chirilsinmi? Bu amalni qaytarib bo‘lmaydi.|Удалить все данные DayX? Это действие нельзя отменить.
Replace existing data with this backup?|Ma’lumotlar ushbu nusxa bilan almashtirilsinmi?|Заменить данные этой резервной копией?
Data imported successfully.|Ma’lumotlar tiklandi.|Данные восстановлены.
Invalid DayX backup file.|DayX nusxa fayli noto‘g‘ri.|Некорректный файл резервной копии DayX.
Connecting…|Ulanmoqda…|Подключение…
Saving…|Saqlanmoqda…|Сохранение…
Saved to server|Saqlangan|Сохранено
Offline · saved on this device|Oflayn · qurilmada saqlangan|Офлайн · сохранено на устройстве
Changes need review|O‘zgarishlarni tekshirish kerak|Нужно проверить изменения
Request failed.|So‘rov bajarilmadi.|Запрос не выполнен.
Email or password is incorrect.|Email yoki parol noto‘g‘ri.|Неверный email или пароль.
This email is already registered. Sign in instead.|Bu email ro‘yxatdan o‘tgan. Hisobingizga kiring.|Этот email уже зарегистрирован. Войдите в аккаунт.
You are already signed in.|Hisobga kirgansiz.|Вы уже вошли.
Refresh the page before trying again.|Qayta urinishdan oldin sahifani yangilang.|Обновите страницу перед повторной попыткой.
Too many attempts. Try again later.|Urinishlar soni ko‘p. Keyinroq qayta urining.|Слишком много попыток. Попробуйте позже.
Too many attempts. Try again in 15 minutes.|Urinishlar soni ko‘p. 15 daqiqadan so‘ng qayta urining.|Слишком много попыток. Попробуйте через 15 минут.
The server could not complete this request.|So‘rov bajarilmadi. Keyinroq qayta urining.|Сервер не смог выполнить запрос. Попробуйте позже.
Session expired · refresh to reconnect|Sessiya tugadi · qayta ulanish uchun yangilang|Сессия истекла · обновите страницу
Your previous account’s plans remain saved on this device.|Avvalgi hisobingizning rejalari ushbu qurilmada saqlanadi.|Планы предыдущего аккаунта остаются сохранёнными на этом устройстве.
Data needs review · export a backup|Ma’lumotlarni tekshiring · nusxani eksport qiling|Проверьте данные · экспортируйте резервную копию
Use a valid email and a password of 8–256 characters.|To‘g‘ri email va 8–256 belgili parol kiriting.|Введите корректный email и пароль длиной 8–256 символов.
Invalid email or password.|Email yoki parol noto‘g‘ri.|Неверный email или пароль.
An account with this email already exists.|Bu email bilan hisob mavjud.|Аккаунт с этим email уже существует.
Connect to the server before resetting data.|Ma’lumotlarni tozalashdan oldin internetga ulaning.|Подключитесь к серверу перед очисткой данных.
Resolve pending changes before resetting.|Tozalashdan oldin saqlanmagan o‘zgarishlarni tekshiring.|Проверьте несохранённые изменения перед сбросом.
Your planner could not open. Refresh the page to try again.|Rejalar ochilmadi. Sahifani yangilab qayta urining.|Не удалось открыть планировщик. Обновите страницу.
Refresh|Yangilash|Обновить
Language|Til|Язык
Choose your interface language.|Interfeys tilini tanlang.|Выберите язык интерфейса.
Monthly plan|Oylik tarif|Месячный тариф
All your tools. One simple price.|Barcha vositalar. Bitta qulay narx.|Все инструменты. Одна простая цена.
/ month|/ oy|/ месяц
Payments are not enabled yet. You can continue using your workspace.|To‘lov hali yoqilmagan. Ish maydonidan foydalanishni davom ettirishingiz mumkin.|Оплата пока не подключена. Продолжайте пользоваться рабочим пространством.
Pricing|Tarif|Тариф
Previous day|Oldingi kun|Предыдущий день
Next day|Keyingi kun|Следующий день
Previous period|Oldingi davr|Предыдущий период
Next period|Keyingi davr|Следующий период
Open menu|Menyuni ochish|Открыть меню
Open exam dates|Imtihonlarni ochish|Открыть экзамены
DayX home|DayX bosh sahifasi|Главная DayX
Workspace navigation|Ish maydoni navigatsiyasi|Навигация рабочего пространства
Skip to content|Asosiy qismga o‘tish|Перейти к содержимому
Skip to planner|Rejalarga o‘tish|Перейти к планам
Opening your planner…|Rejalar ochilmoqda…|Открываем планы…
Opening your account…|Hisob ochilmoqda…|Открываем аккаунт…
Mon–Fri|Du–Ju|Пн–Пт
Sat–Sun|Sh–Ya|Сб–Вс
One shared plan for every weekday|Har bir ish kuni uchun umumiy reja|Общий план на каждый будний день
One shared plan for every Saturday and Sunday|Har shanba va yakshanba uchun umumiy reja|Общий план на каждую субботу и воскресенье
Monday|Dushanba|Понедельник
Tuesday|Seshanba|Вторник
Wednesday|Chorshanba|Среда
Thursday|Payshanba|Четверг
Friday|Juma|Пятница
Saturday|Shanba|Суббота
Sunday|Yakshanba|Воскресенье
Future day|Kelgusi kun|Будущий день
Completed|Bajarilgan|Выполнено
Not completed|Bajarilmagan|Не выполнено
Mark completed|Bajarilgan deb belgilash|Отметить выполненным
Day|Kun|День
Date|Sana|Дата
Upcoming|Yaqin kunlar|Ближайшие дни
Plan overview|Rejalar jadvali|Обзор планов
Your routine, in one clear view.|Kun tartibingiz — yagona jadvalda.|Ваш ритм — в одной таблице.
Track 30 scheduled days. Complete a plan with one click.|30 rejalashtirilgan kunni kuzating. Bajargan rejangizni bir bosishda belgilang.|Следите за 30 запланированными днями. Отмечайте выполненное одним нажатием.
30 scheduled days|30 rejalashtirilgan kun|30 запланированных дней
Future days are available to view; completion opens on the day.|Kelgusi kunlarni ko‘rish mumkin; belgilash o‘sha kuni ochiladi.|Будущие дни доступны для просмотра; отметки открываются в день выполнения.
Plan, focus, grow.|Rejalang. Jamlaning. O‘sing.|Планируйте. Сосредоточьтесь. Растите.
Every day, a step forward.|Har kuni bir qadam oldinga.|Каждый день — шаг вперёд.
Your current plans move into your new account. You can then sign in on another device.|Rejalaringiz yangi hisobga o‘tadi. Keyin boshqa qurilmadan ham kira olasiz.|Планы перенесутся в новый аккаунт. Затем вы сможете войти с другого устройства.
Sign in to open your account’s plans. Your guest workspace remains saved separately on this device.|Rejalaringizni ochish uchun kiring. Mehmon rejalari ushbu qurilmada alohida saqlanadi.|Войдите, чтобы открыть свои планы. Гостевое пространство сохранится отдельно на устройстве.
Toggle preference|Sozlamani o‘zgartirish|Изменить настройку
Toggle loop|Takrorlashni yoqish|Включить повторение
More focus. More time for you.|Kuchli diqqat. O‘zingiz uchun ko‘proq vaqt.|Больше фокуса. Больше времени для себя.
The tools|Imkoniyatlar|Возможности
How it works|Qanday ishlaydi|Как это работает
Your day. Your direction.|Kuningiz. Siz tanlagan yo‘l.|Ваш день. Ваше направление.
Make time for what matters.|Muhim ishlarga vaqt oching.|Найдите время для главного.
Turn your plans into progress with a daily planner, a focus timer and a clear view of your goals. All in DayX.|Kunlik reja, diqqat taymeri va natijalar kuzatuvi bilan maqsadlaringizga yaqinlashing. Barchasi DayX’da.|Приближайтесь к целям с ежедневным планом, таймером фокуса и обзором результатов. Всё в DayX.
Start planning|Rejalashni boshlash|Начать планировать
Explore DayX|DayX’ni sinab ko‘rish|Попробовать DayX
Plans. Focus. Progress.|Reja. Diqqat. Natija.|Планы. Фокус. Прогресс.
One space for a better day.|Yaxshiroq kun uchun yagona maydon.|Одно пространство для лучшего дня.
Less switching. More doing. A set of tools that work together, at your pace.|Kamroq chalg‘ish. Ko‘proq natija. O‘z sur’atingizda birga ishlaydigan vositalar.|Меньше отвлечений. Больше дела. Инструменты, которые работают вместе в вашем темпе.
Plan with intention.|Aniq maqsad bilan rejalang.|Планируйте осознанно.
Build weekday and weekend routines, then see your whole month in a clear plan table.|Ish va dam olish kunlari uchun reja tuzing. Oylik tartibingizni yagona jadvalda ko‘ring.|Создайте планы на будни и выходные. Просматривайте месяц в удобной таблице.
Focus on one thing.|Bitta vazifaga jamlaning.|Сосредоточьтесь на одном.
Pomodoro sessions and automatic study sequences help you protect your time.|Pomodoro va avtomatik o‘qish bosqichlari bilan vaqtingizni samarali sarflang.|Pomodoro и учебные последовательности помогают беречь ваше время.
Make progress visible.|Natijangizni ko‘ring.|Наблюдайте за прогрессом.
Check off completed plans, review your week and keep your exam dates in sight.|Bajarilgan rejalarni belgilang, haftani tahlil qiling va imtihon sanalarini kuzating.|Отмечайте планы, анализируйте неделю и следите за экзаменами.
From an idea to a daily rhythm.|Maqsaddan kunlik odatgacha.|От идеи к ежедневному ритму.
Add your first plan.|Birinchi rejangizni qo‘shing.|Добавьте первый план.
Choose what matters today and give it a place in your routine.|Bugun nima muhimligini tanlang va unga vaqt ajrating.|Выберите главное на сегодня и выделите ему время.
Give it your focus.|Diqqatingizni jamlang.|Уделите этому внимание.
Start a timer. Work at your pace. Take a real break.|Taymerni yoqing. O‘z sur’atingizda ishlang. Vaqtida dam oling.|Запустите таймер. Работайте в своём темпе. Делайте перерывы.
Keep moving forward.|Oldinga intiling.|Продолжайте двигаться.
Your completed plans tell the story. Build on them tomorrow.|Bajarilgan rejalar — natijangiz. Ertaga yana davom eting.|Выполненные планы — ваш результат. Продолжайте завтра.
Simple by design. Fair by price.|Qulay imkoniyatlar. Oddiy narx.|Удобные инструменты. Простая цена.
Your entire daily workspace for one dollar a month.|Butun kunlik ish maydoningiz — oyiga bir dollar.|Всё ваше рабочее пространство — за один доллар в месяц.
DayX monthly|DayX oylik|DayX на месяц
One plan. Every essential tool.|Bitta tarif. Barcha asosiy vositalar.|Один тариф. Все основные инструменты.
Daily and weekly planning|Kunlik va haftalik rejalar|Планы на день и неделю
30-day plan table|30 kunlik rejalar jadvali|Таблица планов на 30 дней
Focus and study timers|Diqqat va o‘qish taymerlari|Таймеры фокуса и учёбы
Exam dates and progress tracking|Imtihonlar va natijalar kuzatuvi|Экзамены и отслеживание прогресса
Uzbek, English and Russian|O‘zbek, ingliz va rus tillari|Узбекский, английский и русский
Create your DayX account|DayX hisobini yaratish|Создать аккаунт DayX
Price preview · payments coming later|Tarif ma’lumoti · to‘lov keyinroq yoqiladi|Обзор тарифа · оплата появится позже
Your next chapter starts today.|Yangi bosqich bugundan boshlanadi.|Ваша новая глава начинается сегодня.
One plan. One focused session. One step closer to your goals.|Bitta reja. Bitta diqqat sessiyasi. Maqsadga yana bir qadam.|Один план. Одна сессия фокуса. Ещё один шаг к цели.
Today’s plan|Bugungi reja|План на сегодня
Read for 20 minutes|20 daqiqa kitob o‘qish|Читать 20 минут
Practice English|Ingliz tilini mashq qilish|Практиковать английский
Work on your main goal|Asosiy maqsad ustida ishlash|Работать над главной целью
Example workspace|Namuna ish maydoni|Пример пространства
Product demo|DayX namoyishi|DayX в действии
DayX in action|DayX amalda|DayX в действии
In action|Amalda|В действии
Guides|Qo‘llanmalar|Руководства
Menu|Menyu|Меню
Explore the demos|Namoyishlarni ko‘rish|Посмотреть демонстрации
Plan table|Rejalar jadvali|Таблица планов
Exam dates|Imtihon sanalari|Даты экзаменов
Start a focus session, take a break and keep a steady rhythm.|Diqqat sessiyasini boshlang, tanaffus qiling va o‘z maromingizda ishlang.|Начните сеанс фокуса, сделайте перерыв и работайте в своём ритме.
Set your session length|Sessiya vaqtini belgilang|Укажите длительность сеанса
Start with one task|Bitta vazifadan boshlang|Начните с одной задачи
Take a break when it is time|Vaqti kelganda tanaffus qiling|Сделайте перерыв вовремя
A clear view of your month.|Butun oy uchun aniq reja.|Все планы на месяц перед глазами.
Keep your routines in one table. Check off completed plans and move between weeks to see how your month is going.|Kun tartibingizni bitta jadvalda ko‘ring. Bajarilgan rejalarni belgilang va haftalar orasida o‘tib, oylik natijangizni kuzating.|Смотрите распорядок в одной таблице. Отмечайте выполненные планы и переключайтесь между неделями, чтобы видеть результаты за месяц.
See your plans by day|Rejalarni kunlar bo‘yicha ko‘ring|Смотрите планы по дням
Mark what you finish|Bajarilganini belgilang|Отмечайте выполненное
Keep your progress in sight|Natijangizni kuzatib boring|Следите за результатом
See what worked this week.|Haftalik natijangizni ko‘ring.|Посмотрите на результаты недели.
Review your week at a glance. Choose a day to see your completed plans and decide what to focus on next.|Haftangizni bir qarashda ko‘ring. Istalgan kunni tanlab, bajarilgan rejalarni ko‘ring va keyingi vazifani belgilang.|Оцените неделю одним взглядом. Выберите день, посмотрите выполненные планы и решите, на чём сосредоточиться дальше.
Compare your days|Kunlar natijasini solishtiring|Сравнивайте результаты дней
Review completed plans|Bajarilgan rejalarni ko‘ring|Просматривайте выполненные планы
Make room for the next step|Keyingi qadamni rejalang|Планируйте следующий шаг
Prepare with a date in mind.|Muhim sanaga tayyor bo‘ling.|Готовьтесь к важной дате.
Add the exam name and choose a date. DayX shows how many days remain, so you can plan your preparation.|Imtihon nomini kiriting va sanani tanlang. DayX qolgan kunlarni ko‘rsatadi — tayyorgarligingizni shunga qarab rejalang.|Введите название экзамена и выберите дату. DayX покажет, сколько дней осталось, чтобы вы могли спланировать подготовку.
Add an important date|Muhim sanani qo‘shing|Добавьте важную дату
See the days remaining|Qancha kun qolganini ko‘ring|Смотрите, сколько дней осталось
Prepare at your own pace|O‘z maromingizda tayyorlaning|Готовьтесь в своём темпе
A few thoughtful extras.|Sizga mos qo‘shimcha qulayliklar.|Дополнительные удобства для вас.
Make the routine yours. Keep your focus close.|Rejani o‘zingizga moslang. Taymer doim yoningizda.|Настройте распорядок под себя. Держите таймер рядом.
See your day take shape.|Kuningiz qanday tartiblanishini ko‘ring.|Посмотрите, как складывается ваш день.
A few simple actions. Everything you need to plan, focus and follow through.|Bir necha oddiy qadam: reja tuzing, diqqat qiling va natijangizni kuzating.|Несколько простых действий: планируйте, сосредоточьтесь и следите за результатом.
Choose a routine that fits your days. Add a plan, set a time and check it off when you finish.|Kunlaringizga mos tartib tanlang. Reja qo‘shing, vaqt belgilang va bajarganingizda belgilab qo‘ying.|Выберите подходящий распорядок. Добавьте план, укажите время и отметьте его после выполнения.
A routine that fits your life.|Hayotingizga mos kun tartibi.|Распорядок под ваш ритм жизни.
Choose any days together. Keep the same plan every day, group similar days, or plan a fresh week.|Istalgan kunlarni birlashtiring. Har kuni bir xil reja qiling, kunlarni guruhlang yoki haftani alohida rejalang.|Объединяйте любые дни. Повторяйте один план, создавайте группы дней или планируйте каждую неделю отдельно.
Choose days. Make them yours.|Kunlarni tanlang. O‘zingizga moslang.|Выберите дни. Настройте под себя.
My workdays|Ish kunlarim|Мои рабочие дни
My rest days|Dam olish kunlarim|Мои дни отдыха
Your plans, at a glance.|Rejalaringiz bir qarashda.|Все планы перед глазами.
See your plans in a clear table. Mark what you finish and follow your progress across the month.|Rejalaringizni qulay jadvalda ko‘ring. Bajarilganlarini belgilang va oylik natijangizni kuzating.|Смотрите планы в удобной таблице. Отмечайте выполненное и следите за результатами в течение месяца.
One check. One step forward.|Bitta belgi. Oldinga bir qadam.|Одна отметка. Один шаг вперёд.
All plans for the current month. Complete a plan with one click.|Joriy oyning barcha rejalari. Bajargan rejangizni bir bosishda belgilang.|Все планы текущего месяца. Отмечайте выполненное одним нажатием.
Choose month|Oyni tanlash|Выбрать месяц
Previous month|Oldingi oy|Предыдущий месяц
Next month|Keyingi oy|Следующий месяц
Month start|Oy boshiga|К началу месяца
Yesterday & today|Kecha va bugun|Вчера и сегодня
Yesterday|Kecha|Вчера
Days of the month|Oy kunlari|Дни месяца
{count} calendar days|{count} kalendar kuni|{count} календарных дней
Open day|Kunni ochish|Открыть день
Week view|Hafta ko‘rinishi|Обзор недели
Full table|To‘liq jadval|Вся таблица
Small steps add up.|Kichik qadamlar natija beradi.|Маленькие шаги складываются в результат.
Keep focus in the corner.|Diqqat taymeri ekran burchagida.|Таймер фокуса в углу экрана.
Open the mini timer while you work in other apps. Resize it, or hide the time when you want fewer distractions.|Boshqa ilovalarda ishlaganda kichik taymerni oching. Hajmini o‘zgartiring yoki chalg‘imaslik uchun vaqtni yashiring.|Откройте мини-таймер для работы в других приложениях. Меняйте размер или скрывайте время, чтобы меньше отвлекаться.
A small timer. More room to work.|Kichik taymer. Ish uchun ko‘proq joy.|Маленький таймер. Больше места для работы.
Focus beyond your browser.|Diqqat brauzerdan tashqarida ham.|Фокус за пределами браузера.
Your next idea|Keyingi g‘oyangiz|Ваша следующая идея
Mini timer|Kichik taymer|Мини-таймер
Stay focused.|Diqqatni saqlang.|Сохраняйте фокус.
On|Yoqilgan|Включён
Keep important dates close.|Muhim sanalar yodingizda.|Важные даты всегда рядом.
Add an exam and its date. See how many days remain, so you can prepare at your own pace.|Imtihon nomi va sanasini kiriting. Qancha kun qolganini ko‘rib, o‘z maromingizda tayyorlaning.|Добавьте экзамен и дату. Смотрите, сколько дней осталось, и готовьтесь в своём темпе.
Add a date. See what is next.|Sanani qo‘shing. Oldindagini ko‘ring.|Добавьте дату. Знайте, что впереди.
Exams|Imtihonlar|Экзамены
English exam|Ingliz tili imtihoni|Экзамен по английскому
Your next milestone|Keyingi muhim sana|Следующая важная дата
Saved. Keep the date in sight.|Saqlandi. Sana doim yodingizda.|Сохранено. Дата всегда перед глазами.
Try it in DayX|DayX’da sinab ko‘rish|Попробовать в DayX
Pause animation|Animatsiyani to‘xtatish|Приостановить анимацию
Play animation|Animatsiyani davom ettirish|Продолжить анимацию
{n} of {total} complete|{total} tadan {n} tasi bajarildi|Выполнено {n} из {total}
Your day, well done.|Bugungi rejalar bajarildi.|Планы на сегодня выполнены.
2 of 3 complete|3 tadan 2 tasi bajarildi|Выполнено 2 из 3
Daily focus|Kunlik diqqat|Фокус на день
Ready for your next step|Keyingi qadamga tayyor|Готовы к следующему шагу
All rights reserved.|Barcha huquqlar himoyalangan.|Все права защищены.
One clear workspace for your plans, focus and progress.|Reja, diqqat va natijalaringiz uchun yagona maydon.|Одно пространство для планов, фокуса и прогресса.

Dashboard|Boshqaruv paneli|Обзор
Open dashboard|Dashboardni ochish|Открыть обзор
Add your priorities|Muhim rejalaringizni qo‘shing|Добавьте важные планы
Repeat at your own pace|O‘z maromingizda takrorlang|Повторяйте в своём ритме
Open the mini timer|Mini taymerni oching|Откройте мини-таймер
Keep working in other apps|Boshqa ilovalarda ishlashda davom eting|Продолжайте работать в других приложениях
Resize it to fit your screen|Ekraningizga moslab kichraytiring|Подберите удобный размер
Reflect|Tahlil|Анализ
Focus|Diqqat|Фокус
Your schedule|Sizning jadvalingiz|Ваше расписание
Choose a date|Sanani tanlang|Выберите дату
Previous month|Oldingi oy|Предыдущий месяц
Next month|Keyingi oy|Следующий месяц
Open a day to view its plans.|Rejalarni ko‘rish uchun kunni tanlang.|Выберите день, чтобы увидеть планы.
Feedback|Fikr va takliflar|Обратная связь
Reports|Xabarlar|Сообщения
Report an issue|Xato haqida yozish|Сообщить об ошибке
Report|Report|Сообщить
Review user reports and update their status.|Foydalanuvchilar xabarlarini ko‘ring va holatini belgilang.|Просматривайте сообщения пользователей и обновляйте их статус.
Found something that does not work? Tell us what happened. Your report goes directly to DayX.|Biror narsa ishlamayaptimi? Nima bo‘lganini yozing. Xabaringiz bevosita DayX’ga yetib boradi.|Что-то не работает? Расскажите, что произошло. Ваше сообщение поступит напрямую в DayX.
Inbox|Kelgan xabarlar|Входящие
Write a report|Xabar yozish|Написать сообщение
Your email|Email manzilingiz|Ваш email
Issue title|Xato nomi|Название проблемы
What went wrong?|Qanday xato yuz berdi?|Что пошло не так?
What happened?|Nima bo‘ldi?|Что произошло?
Describe the steps, what you expected and what happened instead.|Nimalarni bosdingiz, qanday natija kutdingiz va nima yuz berganini yozing.|Опишите шаги, ожидаемый результат и то, что произошло.
Page and screen size are included to help us investigate.|Tekshirish uchun sahifa va ekran o‘lchami ham yuboriladi.|Для проверки также отправляются страница и размер экрана.
Send report|Xabarni yuborish|Отправить сообщение
Sending…|Yuborilmoqda…|Отправка…
Thank you. Your report is received.|Rahmat. Xabaringiz qabul qilindi.|Спасибо. Ваше сообщение получено.
We will review the details. Thank you for helping improve DayX.|Xabaringizni ko‘rib chiqamiz. DayX’ni yaxshilashga yordam berganingiz uchun rahmat.|Мы рассмотрим подробности. Спасибо, что помогаете улучшать DayX.
Reference|Xabar raqami|Номер сообщения
Done|Tayyor|Готово
Could not send the report. Check your connection and try again.|Xabar yuborilmadi. Internetni tekshirib, qayta urinib ko‘ring.|Не удалось отправить сообщение. Проверьте соединение и повторите попытку.
No reports yet|Hozircha xabarlar yo‘q|Сообщений пока нет
Reports from your users will appear here.|Foydalanuvchilarning xabarlari shu yerda ko‘rinadi.|Здесь появятся сообщения пользователей.
Loading reports…|Xabarlar yuklanmoqda…|Загрузка сообщений…
{n} new reports|{n} ta yangi xabar|Новых сообщений: {n}
New|Yangi|Новое
Reviewed|Ko‘rib chiqilgan|Рассмотрено
Resolved|Tuzatilgan|Исправлено
Status|Holat|Статус
Load more|Yana yuklash|Загрузить ещё
Could not load reports. Try again.|Xabarlar yuklanmadi. Qayta urinib ko‘ring.|Не удалось загрузить сообщения. Повторите попытку.
Could not update this report. Try again.|Xabar holati yangilanmadi. Qayta urinib ko‘ring.|Не удалось обновить статус. Повторите попытку.
Enter a valid email address.|To‘g‘ri email manzilini kiriting.|Введите корректный email.
Add a title and describe the issue in at least 10 characters.|Xato nomini va kamida 10 ta belgidan iborat izoh yozing.|Добавьте название и описание не короче 10 символов.
Report limit reached. Please try again tomorrow.|Xabar yuborish limiti tugadi. Ertaga qayta urinib ko‘ring.|Лимит сообщений исчерпан. Попробуйте завтра.
Too many reports. Try again later.|Juda ko‘p xabar yuborildi. Keyinroq urinib ko‘ring.|Слишком много сообщений. Попробуйте позже.
You cannot open these reports.|Bu xabarlarni ko‘rish huquqi yo‘q.|У вас нет доступа к этим сообщениям.
YOUR WORKSPACE|ISH MAYDONINGIZ|ВАШЕ ПРОСТРАНСТВО
TOOLS|BOSHQARUV|УПРАВЛЕНИЕ
A clear view of your plans and progress.|Rejalaringiz va natijalaringiz bir joyda.|Ваши планы и результаты в одном месте.
Total plans|Jami rejalar|Всего планов
Completed|Bajarilgan|Выполнено
Remaining|Qolgan|Осталось
Focused today|Bugungi diqqat|Фокус сегодня
Completion rate: {percent}%|Bajarilish darajasi: {percent}%|Выполнение: {percent}%
Plans to complete|Bajarilishi kerak bo‘lgan rejalar|Планы к выполнению
{count} focus sessions|{count} ta diqqat sessiyasi|Сессий фокуса: {count}
min|daq|мин
Daily plans|Kunlik rejalar|Планы на день
Filter plans|Rejalarni saralash|Фильтр планов
All plans|Barchasi|Все планы
Your first plan starts here|Birinchi rejangizni yarating|Создайте первый план
Add a plan for your weekdays or weekends. Check it off when you’re done.|Ish yoki dam olish kunlari uchun reja qo‘shing. Bajarganingizda belgilab boring.|Добавьте план на будни или выходные. Отмечайте его по мере выполнения.
No completed plans yet|Hali bajarilgan rejalar yo‘q|Пока нет выполненных планов
Your completed plans will appear here.|Bajarilgan rejalar shu yerda ko‘rinadi.|Выполненные планы появятся здесь.
All plans completed|Barcha rejalar bajarildi|Все планы выполнены
You’ve finished every plan for this day.|Bu kun uchun barcha rejalarni bajardingiz.|Вы выполнили все планы на этот день.
Show all plans|Barcha rejalarni ko‘rish|Показать все планы
{done} of {total} plans completed|{total} ta rejadan {done} tasi bajarildi|Выполнено {done} из {total} планов
Focus and upcoming dates|Diqqat va yaqin sanalar|Фокус и ближайшие даты
Break in progress|Tanaffus davom etmoqda|Идёт перерыв
Paused|To‘xtatilgan|Приостановлен
Focus in progress|Diqqat davom etmoqda|Фокус в процессе
Ready to focus|Boshlashga tayyor|Готов к фокусу
One task. Your full attention.|Bitta vazifa. To‘liq diqqat.|Одна задача. Всё ваше внимание.
Resume focus|Diqqatni davom ettirish|Продолжить фокус
Timer settings|Taymer sozlamalari|Настройки таймера
Upcoming exam|Yaqin imtihon|Ближайший экзамен
No upcoming exams|Yaqin imtihonlar yo‘q|Нет ближайших экзаменов
Weekly overview|Haftalik natija|Итоги недели
View week|Haftani ko‘rish|Открыть неделю
Weekly completion|Haftalik bajarilish|Выполнение за неделю
Daily completion this week|Haftadagi kunlik bajarilish|Выполнение по дням недели
By day|Kunlar bo‘yicha|По дням
Full screen|To‘liq ekran|Полноэкранный режим
Continue with email|Email bilan davom etish|Продолжить с email
We'll email you a 6-digit code. No password needed.|Emailingizga 6 xonali kod yuboramiz. Parol shart emas.|Мы отправим 6-значный код на почту. Пароль не нужен.
Send code|Kod yuborish|Отправить код
6-digit code|6 xonali kod|6-значный код
Send a new code|Yangi kod yuborish|Отправить новый код
Change email|Emailni o‘zgartirish|Изменить email
Get a code by email instead|Email orqali kod olish|Получить код по email
Use a password instead|Parol bilan kirish|Войти с паролем
We sent a 6-digit code to {email}.|{email} manziliga 6 xonali kod yubordik.|Мы отправили 6-значный код на {email}.
Enter the 6-digit code from the email.|Xatdagi 6 xonali kodni kiriting.|Введите 6-значный код из письма.
Checking…|Tekshirilmoqda…|Проверяем…
Email codes are not available yet.|Email kodi hozircha mavjud emas.|Вход по коду пока недоступен.
A code was just sent. Check your inbox.|Kod hozirgina yuborildi. Pochtangizni tekshiring.|Код только что отправлен. Проверьте почту.
This code has expired. Request a new one.|Kod muddati tugagan. Yangisini so‘rang.|Срок действия кода истёк. Запросите новый.
The code is incorrect.|Kod noto‘g‘ri.|Неверный код.
Too many wrong codes. Request a new one.|Juda ko‘p noto‘g‘ri urinish. Yangi kod so‘rang.|Слишком много неверных попыток. Запросите новый код.
We couldn't complete Google sign-in.|Google orqali kirish yakunlanmadi.|Не удалось завершить вход через Google.
Exit full screen|To‘liq ekrandan chiqish|Выйти из полноэкранного режима
Block {current} of {total}|Blok {current} / {total}|Блок {current} из {total}
Time is hidden|Vaqt yashirilgan|Время скрыто
Focus task|Diqqat vazifasi|Задача для фокуса
Skip to next phase|Keyingi bosqichga o‘tish|Перейти к следующему этапу
Sound and alerts|Ovoz va bildirishnomalar|Звук и уведомления
Listen|Tinglash|Прослушать
{count} sessions · {minutes} min|{count} ta sessiya · {minutes} daq|Сессий: {count} · {minutes} мин
Break|Tanaffus|Перерыв
Minutes; × is how many times it repeats.|Daqiqalarda; × — necha marta takrorlanishi.|В минутах; × — сколько раз повторить.
{count} tasks · {minutes} min|{count} ta vazifa · {minutes} daq|Задач: {count} · {minutes} мин
Minutes for {name}|{name} uchun daqiqalar|Минуты для: {name}
Tick the tasks, set minutes, and DayX runs them one after another.|Vazifalarni belgilang, daqiqasini kiriting — DayX ularni ketma-ket yuritadi.|Отметьте задачи и укажите минуты — DayX запустит их по очереди.
Deep work tasks include a short break inside their time.|Chuqur diqqat vazifalarida qisqa tanaffus shu vaqt ichiga kiradi.|В задачах глубокой работы короткий перерыв входит в их время.
Last {count} days|Oxirgi {count} kun|Последние {count} дн.
{count} remaining|{count} ta belgilanmagan|Осталось: {count}
Future day|Kelgusi kun|Будущий день
Mon|Du|Пн
Tue|Se|Вт
Wed|Ch|Ср
Thu|Pa|Чт
Fri|Ju|Пт
Sat|Sh|Сб
Sun|Ya|Вс
Edit {name}|{name} rejasini tahrirlash|Редактировать: {name}
Delete {name}|{name} rejasini o‘chirish|Удалить: {name}
Clean surfaces, slate tones and a clear blue accent.|Sodda ko‘rinish, grafit tuslari va aniq ko‘k aksent.|Чистые поверхности, графитовые тона и синий акцент.
Slate and Blue color palette|Grafit va ko‘k ranglar palitrasi|Графитовая и синяя палитра
Slate|Grafit|Графит
Blue|Ko‘k|Синий
Cloud|Oq|Белый
Slate & Blue|Grafit va ko‘k|Графит и синий
White surfaces, soft grays and clear black accents.|Oq fon, yumshoq kulrang tuslar va aniq qora aksentlar.|Белые поверхности, мягкие серые тона и чёрные акценты.
Monochrome color palette|Oq, kulrang va qora palitra|Белая, серая и чёрная палитра
Black|Qora|Чёрный
Gray|Kulrang|Серый
White|Oq|Белый
Monochrome|Oq, kulrang va qora|Белый, серый и чёрный
Go to|O‘tish|Перейти
Actions|Amallar|Действия
Plans|Rejalar|Планы
Reset focus timer|Taymerni qayta boshlash|Сбросить таймер
Go to today|Bugunga o‘tish|Перейти к сегодня
Export data|Ma’lumotlarni yuklab olish|Экспорт данных
No results|Hech narsa topilmadi|Ничего не найдено
Try a different word.|Boshqa so‘z bilan qidirib ko‘ring.|Попробуйте другое слово.
Command menu|Buyruqlar paneli|Командное меню
Search plans, pages and actions…|Reja, sahifa yoki amalni qidiring…|Поиск планов, страниц и действий…
Results|Natijalar|Результаты
to navigate|tanlash|выбрать
to open|ochish|открыть
Undo|Qaytarish|Отменить
Plan deleted.|Reja o‘chirildi.|План удалён.
Plan restored.|Reja qaytarildi.|План восстановлен.
Exam restored.|Imtihon qaytarildi.|Экзамен восстановлен.
General|Umumiy|Общее
Open the command menu|Buyruqlar panelini ochish|Открыть командное меню
Undo the last deletion|Oxirgi o‘chirishni qaytarish|Отменить последнее удаление
Close a window|Oynani yopish|Закрыть окно
Plans and days|Rejalar va kunlar|Планы и дни
Owner|Egasi|Владелец
Start or pause|Boshlash yoki to‘xtatish|Запустить или приостановить
Letter shortcuts pause while you type in a field.|Maydonga yozayotganingizda harfli tugmalar ishlamaydi.|Буквенные клавиши не срабатывают, пока вы печатаете в поле.
Search|Qidirish|Поиск
Back to log in|Kirish sahifasiga qaytish|Вернуться ко входу
ACCOUNT RECOVERY|HISOBNI TIKLASH|ВОССТАНОВЛЕНИЕ ДОСТУПА
Reset your password.|Parolni tiklash.|Сброс пароля.
Enter your account email and we’ll send you a link to set a new password.|Hisobingiz emailini kiriting — yangi parol o‘rnatish uchun havola yuboramiz.|Введите email аккаунта — мы отправим ссылку для нового пароля.
Send reset link|Havola yuborish|Отправить ссылку
Check your email.|Emailingizni tekshiring.|Проверьте почту.
If an account exists for {email}, a reset link is on its way. It works for 1 hour.|Agar {email} bilan hisob mavjud bo‘lsa, havola yuborildi. U 1 soat amal qiladi.|Если аккаунт с {email} существует, ссылка уже в пути. Она действует 1 час.
This link is incomplete.|Havola to‘liq emas.|Ссылка неполная.
Open the newest link from your email, or request a new one.|Emaildagi eng so‘nggi havolani oching yoki yangisini so‘rang.|Откройте самую новую ссылку из письма или запросите новую.
Request a new link|Yangi havola so‘rash|Запросить новую ссылку
Set a new password.|Yangi parol o‘rnating.|Задайте новый пароль.
Choose a password you haven’t used here before. Other devices will be signed out.|Avval ishlatilmagan parol tanlang. Boshqa qurilmalarda hisobdan chiqariladi.|Выберите пароль, который здесь ещё не использовался. На других устройствах будет выполнен выход.
New password|Yangi parol|Новый пароль
Save new password|Yangi parolni saqlash|Сохранить пароль
Use a password of 8–256 characters.|Parol 8–256 belgidan iborat bo‘lsin.|Пароль должен содержать 8–256 символов.
EMAIL CONFIRMATION|EMAILNI TASDIQLASH|ПОДТВЕРЖДЕНИЕ EMAIL
Confirming your email…|Email tasdiqlanmoqda…|Подтверждаем email…
Email confirmed.|Email tasdiqlandi.|Email подтверждён.
Thank you. Your account is now fully set up.|Rahmat! Hisobingiz to‘liq sozlandi.|Спасибо! Ваш аккаунт полностью настроен.
Continue to your workspace|Ish maydoniga o‘tish|Перейти в пространство
We couldn’t confirm your email.|Emailni tasdiqlab bo‘lmadi.|Не удалось подтвердить email.
You can send a new link from your account window.|Hisob oynasidan yangi havola yuborishingiz mumkin.|Новую ссылку можно отправить из окна аккаунта.
By creating an account, you agree to the {terms} and {privacy}.|Hisob ochish orqali siz {terms} va {privacy}ga rozilik bildirasiz.|Создавая аккаунт, вы соглашаетесь с {terms} и {privacy}.
Terms of use|Foydalanish shartlari|Условия использования
the Terms of use|Foydalanish shartlari|Условиями использования
Privacy policy|Maxfiylik siyosati|Политика конфиденциальности
the Privacy policy|Maxfiylik siyosati|Политикой конфиденциальности
Forgot your password?|Parolni unutdingizmi?|Забыли пароль?
Reset password|Parolni tiklash|Сброс пароля
Confirm email|Emailni tasdiqlash|Подтверждение email
Enter your password to confirm|Tasdiqlash uchun parolingizni kiriting|Введите пароль для подтверждения
Type {email} to confirm|Tasdiqlash uchun {email} ni yozing|Введите {email} для подтверждения
Your email isn’t confirmed yet. Confirm it so you can always recover your account.|Emailingiz hali tasdiqlanmagan. Hisobingizni har doim tiklay olishingiz uchun uni tasdiqlang.|Ваш email ещё не подтверждён. Подтвердите его, чтобы всегда иметь возможность восстановить аккаунт.
Send confirmation email|Tasdiqlash xatini yuborish|Отправить письмо для подтверждения
Delete account|Hisobni o‘chirish|Удалить аккаунт
This permanently deletes your account, plans and history. It cannot be undone. Export your data from Settings first if you want a copy.|Hisobingiz, rejalaringiz va tarixingiz butunlay o‘chiriladi. Buni qaytarib bo‘lmaydi. Nusxa kerak bo‘lsa, avval Sozlamalardan ma’lumotlarni yuklab oling.|Аккаунт, планы и история будут удалены навсегда. Это нельзя отменить. Если нужна копия, сначала экспортируйте данные в настройках.
Delete my account permanently|Hisobimni butunlay o‘chirish|Удалить аккаунт навсегда
Email sent. Check your inbox.|Xat yuborildi. Pochtangizni tekshiring.|Письмо отправлено. Проверьте почту.
Your account and its data were deleted.|Hisobingiz va ma’lumotlaringiz o‘chirildi.|Ваш аккаунт и данные удалены.
Password reset by email is not available yet.|Email orqali parolni tiklash hozircha mavjud emas.|Сброс пароля по email пока недоступен.
Email confirmation is not available yet.|Emailni tasdiqlash hozircha mavjud emas.|Подтверждение email пока недоступно.
This reset link is invalid or has expired. Request a new one.|Bu havola noto‘g‘ri yoki muddati o‘tgan. Yangisini so‘rang.|Ссылка недействительна или устарела. Запросите новую.
This account no longer exists.|Bu hisob endi mavjud emas.|Этот аккаунт больше не существует.
Sign in first.|Avval hisobingizga kiring.|Сначала войдите в аккаунт.
A confirmation email was just sent. Check your inbox.|Tasdiqlash xati hozirgina yuborildi. Pochtangizni tekshiring.|Письмо только что отправлено. Проверьте почту.
This confirmation link is invalid or has expired.|Tasdiqlash havolasi noto‘g‘ri yoki muddati o‘tgan.|Ссылка для подтверждения недействительна или устарела.
Type your account email to confirm.|Tasdiqlash uchun hisobingiz emailini yozing.|Введите email аккаунта для подтверждения.
Password is incorrect.|Parol noto‘g‘ri.|Неверный пароль.
`
    .trim()
    .split("\n")
    .map((line) => {
      const [key, uz, ru] = line.split("|");
      return [key, { uz, ru, en: key }];
    }),
);

export function t(key, values = {}) {
  return translateInLanguage(key, language, values);
}

export function translateInLanguage(key, locale, values = {}) {
  const text = copy[key]?.[locale] ?? key;
  return text.replace(/\{(\w+)\}/g, (token, name) => values[name] ?? token);
}

// For legacy HTML-producing views, translate only their static copy fragments.
// Dynamic values (names, notes, schedules, emails) are never passed through this.
export function l(fragment) {
  if (language === "en") return fragment;
  const translate = (value) =>
    value.replace(
      /^(\s*)([\s\S]*?)(\s*)$/,
      (_, before, text, after) => before + t(text) + after,
    );
  return fragment
    .replace(
      /\b(placeholder|aria-label|title)="([^"]*)"/g,
      (_, attr, text) => `${attr}="${translate(text)}"`,
    )
    .replace(
      /(^|>)([^<>]*)(?=<|$)/g,
      (_, prefix, text) => prefix + translate(text),
    );
}
// A page that carries its own language (a guide at /ru/...) shows it without
// overriding the visitor's choice; picking from the menu remembers it.
export function setLanguage(next, { remember = true } = {}) {
  if (!Object.hasOwn(languages, next) || language === next) return;
  language = next;
  if (remember)
    try {
      localStorage.setItem(LANGUAGE_KEY, language);
      localStorage.setItem(LANGUAGE_SOURCE_KEY, "user");
    } catch {}
  document.documentElement.lang = language;
  window.dispatchEvent(new CustomEvent("dayx:language"));
}
export function languageSelect() {
  return `<div class="language-control"><button type="button" class="language-trigger" data-language-trigger aria-label="${t("Language")}: ${languages[language]}" aria-haspopup="menu" aria-expanded="false">${icon("globe")}<span data-language-label>${languages[language]}</span><span class="language-chevron">${icon("chevron")}</span></button></div>`;
}
export function localizeShell() {
  document.documentElement.lang = language;
  document.querySelectorAll("[data-i18n]").forEach((el) => {
    el.textContent = t(el.dataset.i18n);
  });
  document.querySelectorAll("[data-i18n-label]").forEach((el) => {
    el.setAttribute("aria-label", t(el.dataset.i18nLabel));
  });
  document.querySelectorAll("[data-language-trigger]").forEach((trigger) => {
    trigger.querySelector("[data-language-label]").textContent =
      languages[language];
    trigger.setAttribute(
      "aria-label",
      `${t("Language")}: ${languages[language]}`,
    );
  });
}
export function bindLanguage() {
  localizeShell();
  let menu = null;
  let activeTrigger = null;
  const close = (restoreFocus = false) => {
    menu?.remove();
    menu = null;
    activeTrigger?.setAttribute("aria-expanded", "false");
    activeTrigger?.removeAttribute("aria-controls");
    if (restoreFocus && activeTrigger?.isConnected) activeTrigger.focus();
    activeTrigger = null;
  };
  const position = () => {
    if (!menu || !activeTrigger?.isConnected) return close();
    const rect = activeTrigger.getBoundingClientRect();
    if (!rect.width || rect.bottom < 0 || rect.top > window.innerHeight)
      return close();
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;
    const alignRight = activeTrigger.closest(
      ".dayx-landing-nav, .dayx-footer, .auth-nav",
    );
    const left = Math.max(
      12,
      Math.min(
        alignRight ? rect.right - width : rect.left,
        window.innerWidth - width - 12,
      ),
    );
    const top =
      rect.bottom + height + 8 <= window.innerHeight - 12
        ? rect.bottom + 8
        : Math.max(12, rect.top - height - 8);
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  };
  const open = (trigger) => {
    if (trigger === activeTrigger) return close();
    close();
    activeTrigger = trigger;
    menu = document.createElement("div");
    menu.id = "dayxLanguageMenu";
    menu.className = "language-menu";
    menu.classList.toggle(
      "language-menu-dark",
      !!trigger.closest(".sidebar, .dayx-landing-nav, .dayx-footer"),
    );
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", t("Language"));
    menu.innerHTML = `<div class="language-menu-heading" aria-hidden="true">${t("Language")}</div>${Object.entries(
      languages,
    )
      .map(
        ([id, label]) =>
          `<button type="button" class="language-option" role="menuitemradio" aria-checked="${id === language}" tabindex="-1" data-language-value="${id}"><span class="language-code" aria-hidden="true">${id.toUpperCase()}</span><span lang="${id}">${label}</span><span class="language-check" aria-hidden="true">${id === language ? icon("check") : ""}</span></button>`,
      )
      .join("")}`;
    // Render above the app so the sidebar's overflow never clips the menu.
    document.body.append(menu);
    position();
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-controls", menu.id);
    menu.addEventListener("click", (event) => {
      event.stopPropagation();
      const option = event.target.closest("[data-language-value]");
      if (!option) return;
      const index = [
        ...document.querySelectorAll("[data-language-trigger]"),
      ].indexOf(activeTrigger);
      const next = option.dataset.languageValue;
      close();
      setLanguage(next);
      document
        .querySelectorAll("[data-language-trigger]")
        [index]?.focus({ preventScroll: true });
    });
    menu.querySelector('[aria-checked="true"]').focus({ preventScroll: true });
  };
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-language-trigger]");
    if (trigger) open(trigger);
    else if (!menu?.contains(event.target)) close();
  });
  document.addEventListener("keydown", (event) => {
    const trigger = event.target.closest("[data-language-trigger]");
    if (trigger && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      open(trigger);
      return;
    }
    if (!menu) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopImmediatePropagation();
      close(true);
    } else if (event.key === "Tab") {
      // Return to the trigger so tabbing follows the page's normal order.
      close(true);
    } else if (
      menu.contains(event.target) &&
      ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
    ) {
      event.preventDefault();
      const options = [...menu.querySelectorAll("[role='menuitemradio']")];
      const current = options.indexOf(document.activeElement);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? options.length - 1
            : (current +
                (event.key === "ArrowDown" ? 1 : -1) +
                options.length) %
              options.length;
      options[next].focus();
    }
  });
  window.addEventListener("resize", () => close());
  window.addEventListener(
    "scroll",
    (event) => {
      if (menu && !menu.contains(event.target)) position();
    },
    true,
  );
  window.addEventListener("dayx:language", () => close());
  window.addEventListener("popstate", () => close());
}
